import { DatasetId } from "@intelligems/ig-types";
import { uppercaseSnakeToNormal } from "@intelligems/ig-utils";

import theme from "../../../../theme";

type DataType =
  | "numberNoDecimal"
  | "number"
  | "currency"
  | "currencyNoDecimal"
  | "percent"
  | "percentTwoDecimal"
  | "numberNoRounding"
  | "none";

export type GraphConfig = {
  title: string[];
  metrics: string[];
  dataTypes: DataType[];
  color: string;
  formatter?: (v: number, tick: boolean) => string;
  datasetId: DatasetId;
  isImpactGraph?: boolean;
  allowThreeRow?: boolean;
  graphType: GraphType;
  isGroup: boolean;
  stackedBarLegendTitles?: string[];
  showAllStackedLabels?: boolean;
  showSumStackedLabels?: boolean;
  isFullWidth?: boolean;
  showStackedPercentage?: boolean;
  addTopPadding?: boolean;
  filterMetric?: string;
  sortMetric?: string;
  autoYaxisMax?: boolean;
  autoYAxisMin?: boolean;
  histogramBucket?: string;
  histogramLabelFormat?: string;
  sortOrder?: string;
  showSumMetric?: boolean;
  isLossMetric?: "profit" | "revenue";
  hasMetricFilterIndex?: boolean; // has two metrics to switch between
  yAxisLabel?: string[]; // show y-axis label
  yAxisDataMin?: number;
  formatTooltipLabel?: boolean; // if true, graphValueFormatter will be used on the tooltip label (linechart only)
  showProfitPlaceholder?: boolean;
  xAxisLabel?: string[]; // show x-axis label
  showLegend?: boolean; // hooked up to line graph currently

  // sitewide
  showMoreDetails?: boolean;
  sumMetrics?: { key: string; label: string }[];
  metricTableColumnKeys?: string[];
  legendFormatFunction?: (string) => string;
};

export type GraphConfigGroup = {
  groupTitle: string;
  configs: GraphConfig[];
  isGroup: boolean;
  showFilter?: boolean;
  showGroupTitle?: boolean;
  groupFilterMetric?: "VARIATION_ID";
  filterListSubheader?: string;
};

export type DashboardGraphConfigs = (GraphConfig | GraphConfigGroup)[];

export enum GraphType {
  Histogram = "histogram",
  StackedBar = "stackedBar",
  Bar = "bar",
  StackedArea = "stackedArea",
  SyncedBar = "syncedBar",
  ClusteredBar = "clusteredBar",
  BiaxialBar = "biaxialBar",
  Line = "line",
}
export const singlePersonalizationTotal: DashboardGraphConfigs = [
  {
    title: ["Orders"],
    metrics: ["ORDERS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "singleCampaignTotal",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
    allowThreeRow: true,
  },
];

export const overviewTableCompareBaseConfig: DashboardGraphConfigs = [
  {
    title: ["Orders"],
    metrics: ["ORDERS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "compareDeviceType",
    graphType: GraphType.Line,
    filterMetric: "DEVICE_TYPE",
    isGroup: false,
    showSumMetric: true,
    allowThreeRow: true,
  },
];

export const dataReconciliationGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Orders"],
    metrics: ["ORDERS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "ordersReconciliation",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
    allowThreeRow: true,
  },
];

export const alwaysOnTotals: DashboardGraphConfigs = [
  {
    title: ["Orders"],
    metrics: ["ORDERS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnCumulative",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
    allowThreeRow: true,
  },
];

export const personalizationMainMetricsGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Orders"],
    metrics: ["ORDERS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Visitors"],
    metrics: ["VISITORS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },

  {
    title: ["Conversion Rate"],
    metrics: ["REDEMPTION_RATE"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["AOV"],
    metrics: ["AOV"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },

  {
    title: ["Total Revenue"],
    metrics: ["TOTAL_REVENUE"],
    dataTypes: ["currencyNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Total Profit"],
    metrics: ["TOTAL_PROFIT"],
    dataTypes: ["currencyNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
];

export const personalizationOfferGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Orders Discounted"],
    metrics: ["ORDERS_DISCOUNTED"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Percent Orders Discounted"],
    metrics: ["PCT_ORDERS_DISCOUNTED"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Total Discount Offered"],
    metrics: ["TOTAL_DISCOUNT"],
    dataTypes: ["currencyNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Discount % of Revenue (pre-discount)"],
    metrics: ["DISCOUNT_PCT_REVENUE"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Discount Percent Profit"],
    metrics: ["DISCOUNT_PCT_PROFIT"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
  {
    title: ["Average Discount"],
    metrics: ["AVG_DISCOUNT"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "campaignOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    showSumMetric: true,
  },
];

export const productGroupsGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Revenue by Product Group"],
    metrics: ["PCT_TEST_GROUP_REVENUE"],
    filterMetric: "PRODUCT_GROUP",
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "productGroups",
    graphType: GraphType.StackedBar,
    isGroup: false,
    showAllStackedLabels: true,
    showStackedPercentage: true,
    isFullWidth: true,
    sortMetric: "REVENUE_PER_VISITOR",
  },
  {
    title: ["Units by Product Group"],
    metrics: ["PCT_TEST_GROUP_UNITS"],
    filterMetric: "PRODUCT_GROUP",
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "productGroups",
    graphType: GraphType.StackedBar,
    isGroup: false,
    showAllStackedLabels: true,
    showStackedPercentage: true,
    isFullWidth: true,
    sortMetric: "REVENUE_PER_VISITOR",
  },
  {
    groupTitle: "Units",
    isGroup: true,
    showGroupTitle: false,
    showFilter: true,
    configs: [
      {
        title: ["Revenue per Visitor"],
        metrics: ["REVENUE_PER_VISITOR"],
        dataTypes: ["currency"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
      },
      {
        title: ["Profit per Visitor"],
        metrics: ["PROFIT_PER_VISITOR"],
        dataTypes: ["currency"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
        showProfitPlaceholder: true,
      },
      {
        title: ["Units Sold"],
        metrics: ["NUM_UNITS"],
        dataTypes: ["numberNoDecimal"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
      },
      {
        title: ["% Total Units"],
        metrics: ["PCT_TEST_GROUP_UNITS"],
        dataTypes: ["percent"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
      },
      {
        title: ["Revenue per Unit"],
        metrics: ["AVG_REV_PER_UNIT"],
        dataTypes: ["currency"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
      },
      {
        title: ["Profit per Unit"],
        metrics: ["AVG_PROFIT_PER_UNIT"],
        dataTypes: ["currency"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
        showProfitPlaceholder: true,
      },
      {
        title: ["Average Units Ordered"],
        metrics: ["AVG_UNITS_ORDERED"],
        dataTypes: ["none"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
      },
      {
        title: ["Average Units per Visitor"],
        metrics: ["UNITS_PER_VISITOR"],
        dataTypes: ["none"],
        color: theme.palette.info.main,
        datasetId: "productGroups",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "PRODUCT_GROUP",
      },
    ],
  },
];

export const shippingGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Shipping Revenue per Order"],
    metrics: ["AVG_SHIPPING_REVENUE_PER_ORDER"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    title: ["Percent of Orders with Free Shipping"],
    metrics: ["PCT_ORDERS_FREE_SHIPPING"],
    dataTypes: ["percent"],
    color: theme.palette.info.main,
    datasetId: "freeShipping",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    groupTitle: "Shipping Rate Detail",
    showFilter: true,
    isGroup: true,
    filterMetric: "SHIPPING_METHOD",
    filterListSubheader: "Most to Least Revenue:",
    configs: [
      {
        title: ["Orders by Shipping Method"],
        metrics: ["ORDERS_BY_RATE_NAME"],
        dataTypes: ["numberNoDecimal"],
        color: theme.palette.info.main,
        datasetId: "shippingMethod",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "SHIPPING_METHOD",
      },
      {
        title: ["Average Shipping Revenue by Method"],
        metrics: ["AVG_SHIPPING_REVENUE_BY_RATE_NAME"],
        dataTypes: ["currency"],
        color: theme.palette.info.main,
        datasetId: "shippingMethod",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "SHIPPING_METHOD",
      },
    ],
  },
];

export const impactGraphConfig: DashboardGraphConfigs = [
  {
    title: ["Revenue per Site Visitor"],
    metrics: ["REV_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isImpactGraph: true,
    isGroup: false,
  },
  {
    title: ["Profit per Site Visitor"],
    metrics: ["PROFIT_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isImpactGraph: true,
    isGroup: false,
    showProfitPlaceholder: true,
  },
];

export const overviewGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Conversion Rate"],
    metrics: ["CVR"],
    dataTypes: ["percentTwoDecimal"],
    color: theme.palette.primary.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isImpactGraph: true,
    isGroup: false,
  },
  {
    title: ["Revenue per Site Visitor"],
    metrics: ["REV_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isImpactGraph: true,
    isGroup: false,
  },
  {
    title: ["Profit per Site Visitor"],
    metrics: ["PROFIT_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isImpactGraph: true,
    isGroup: false,
    showProfitPlaceholder: true,
  },
  {
    title: ["AOV Breakdown"],
    metrics: [
      "AVG_PRODUCT_REVENUE_PER_ORDER",
      "AVG_SHIPPING_REVENUE_PER_ORDER",
    ],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.StackedBar,
    stackedBarLegendTitles: [
      "Product Revenue Per Order",
      "Shipping Revenue Per Order",
    ],
    isGroup: false,
    showSumStackedLabels: true,
    addTopPadding: true,
    autoYAxisMin: true,
    allowThreeRow: true,
  },
  {
    title: ["Visitors"],
    metrics: ["VISITORS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    allowThreeRow: true,
  },
  {
    title: ["Orders"],
    metrics: ["ORDERS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    allowThreeRow: true,
  },
];

export const revenueGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Revenue per Site Visitor"],
    metrics: ["REV_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    title: ["AOV Breakdown"],
    metrics: [
      "AVG_PRODUCT_REVENUE_PER_ORDER",
      "AVG_SHIPPING_REVENUE_PER_ORDER",
    ],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.StackedBar,
    stackedBarLegendTitles: [
      "Product Revenue Per Order",
      "Shipping Revenue Per Order",
    ],
    isGroup: false,
    showSumStackedLabels: true,
    addTopPadding: true,
    autoYAxisMin: true,
  },
  {
    title: ["Average Units per Order"],
    metrics: ["AVG_UNITS_PER_ORDER"],
    dataTypes: ["none"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    title: ["Average Product Revenue per Unit"],
    metrics: ["AVG_PRODUCT_REV_PER_UNIT"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
  },
];

export const visitorsGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Views over Time"],
    isFullWidth: true,
    metrics: ["DAY_VIEWS"],
    dataTypes: ["none"],
    color: theme.palette.error.main,
    datasetId: "viewsOverTime",
    graphType: GraphType.StackedArea,
    filterMetric: "EVENT_TIME",
    isGroup: false,
  },
  {
    isGroup: false,
    title: ["Conversion Funnel"],
    metrics: ["ATC_RATE", "BEGIN_CHECKOUT_RATE", "CVR"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "cartProgression",
    graphType: GraphType.Line,
    showLegend: true,
  },
  {
    title: ["Visitors"],
    metrics: ["VISITORS"],
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    groupTitle: "Custom Events",
    showGroupTitle: true,
    showFilter: true,
    isGroup: true,
    filterMetric: "EVENT_NAME",
    configs: [
      {
        title: ["% of Visitors"],
        metrics: ["PCT_VISITORS"],
        dataTypes: ["percent"],
        color: theme.palette.info.main,
        datasetId: "customEventGoals",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "EVENT_NAME",
      },
      {
        title: ["Number of Visitors"],
        metrics: ["N_VISITORS"],
        dataTypes: ["numberNoDecimal"],
        color: theme.palette.info.main,
        datasetId: "customEventGoals",
        graphType: GraphType.SyncedBar,
        isGroup: false,
        filterMetric: "EVENT_NAME",
      },
    ],
  },
];

export const profitGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Gross Margin"],
    metrics: ["TOTAL_MARGIN"],
    dataTypes: ["percent"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    showProfitPlaceholder: true,
  },
  {
    title: ["Profit per Site Visitor"],
    metrics: ["PROFIT_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.info.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    showProfitPlaceholder: true,
  },
];

export const ordersGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Orders over Time"],
    metrics: ["DAY_ORDERS"],
    dataTypes: ["none"],
    color: theme.palette.error.main,
    datasetId: "ordersOverTime",
    graphType: GraphType.StackedArea,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    isFullWidth: true,
  },
  {
    title: ["Order Value Histogram"],
    metrics: ["AOV_BUCKET_COUNT"],
    histogramBucket: "AOV_BUCKET",
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "aovHistogram",
    graphType: GraphType.Histogram,
    isGroup: false,
    isFullWidth: true,
    histogramLabelFormat: "currency",
  },
  {
    title: ["% Orders by Unit Quantity"],
    metrics: ["PCT_TEST_GROUP_ORDERS"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "unitQuantityComposition",
    graphType: GraphType.StackedBar,
    isGroup: false,
    showAllStackedLabels: true,
    showStackedPercentage: true,
    filterMetric: "UNIT_BUCKET",
    sortMetric: "NUM_ORDERS",
    sortOrder: "ascending",
  },
  {
    title: ["% Net Revenue by Order Unit Quantity"],
    metrics: ["PCT_TEST_GROUP_REVENUE"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "unitQuantityComposition",
    graphType: GraphType.StackedBar,
    isGroup: false,
    showAllStackedLabels: true,
    showStackedPercentage: true,
    filterMetric: "UNIT_BUCKET",
    sortMetric: "NUM_ORDERS",
    sortOrder: "ascending",
  },
  {
    title: ["Unit Quantity Histogram"],
    metrics: ["UNIT_QUANTITY_BUCKET_COUNT"],
    histogramBucket: "UNIT_QUANTITY_BUCKET",
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "unitQuantityHistogram",
    graphType: GraphType.Histogram,
    isGroup: false,
    isFullWidth: true,
    histogramLabelFormat: "number",
  },
];

export const discountsGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["Orders / Percent Total Orders"],
    metrics: ["FILTERED_NUM_ORDERS", "PCT_TOTAL_ORDERS"],
    dataTypes: ["numberNoDecimal", "percent"],
    color: theme.palette.error.main,
    datasetId: "discounts",
    stackedBarLegendTitles: ["Orders", "Percentage"],
    graphType: GraphType.BiaxialBar,
    isGroup: false,
    showAllStackedLabels: true,
  },
  {
    title: ["Average Offer per Order"],
    metrics: ["AVG_DISCOUNT_PER_ORDER_FILTERED", "AVG_DISCOUNT_PER_ORDER_ALL"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "discounts",
    graphType: GraphType.ClusteredBar,
    stackedBarLegendTitles: ["Discounted Orders", "All Orders"],
    isGroup: false,
  },
  {
    title: ["% of Gross Revenue (pre-discount)"],
    metrics: ["DISCOUNT_PCT_FILTERED_AOV", "DISCOUNT_PCT_TOTAL_AOV"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "discounts",
    graphType: GraphType.ClusteredBar,
    stackedBarLegendTitles: ["Discounted Orders", "All Orders"],
    isGroup: false,
    autoYAxisMin: true,
  },
  {
    title: ["% of Gross Profit (pre-discount)"],
    metrics: ["DISCOUNT_PCT_FILTERED_PPO", "DISCOUNT_PCT_TOTAL_PPO"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "discounts",
    graphType: GraphType.ClusteredBar,
    isGroup: false,
    stackedBarLegendTitles: ["Discounted Orders", "All Orders"],
    autoYAxisMin: true,
    showProfitPlaceholder: true,
  },
  {
    title: ["Offer Histogram"],
    metrics: ["DISCOUNT_BUCKET_COUNT"],
    histogramBucket: "DISCOUNT_BUCKET",
    dataTypes: ["numberNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "discountHistogram",
    graphType: GraphType.Histogram,
    isGroup: false,
    isFullWidth: true,
    histogramLabelFormat: "currency",
  },
];

export const timeSeriesGraphsConfig: DashboardGraphConfigs = [
  {
    title: ["CVR Over Time"],
    metrics: ["CONVERSION"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "cumulativeRevenueProfitOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    isFullWidth: true,
    showLegend: true,
  },
  {
    title: ["AOV Over Time"],
    metrics: ["AOV"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "cumulativeRevenueProfitOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    isFullWidth: true,
    showLegend: true,
  },
  {
    title: ["Revenue Per Visitor Over Time"],
    metrics: ["REVENUE_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "cumulativeRevenueProfitOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    isFullWidth: true,
    showLegend: true,
  },
  {
    title: ["Profit Per Visitor Over Time"],
    metrics: ["PROFIT_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "cumulativeRevenueProfitOverTime",
    graphType: GraphType.Line,
    filterMetric: "EVENT_TIME",
    isGroup: false,
    isFullWidth: true,
    showProfitPlaceholder: true,
    showLegend: true,
  },
];

export const statSigGraphsConfig: DashboardGraphConfigs = [
  {
    title: [
      "Revenue per Visitor Probability to be Best",
      "Revenue per Visitor Probability to beat Control",
    ],
    metrics: ["revenue_per_visitor_best", "revenue_per_visitor_beat_control"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    hasMetricFilterIndex: true,
  },
  {
    title: [
      "Profit per Visitor Probability to be Best",
      "Profit per Visitor Probability to beat Control",
    ],
    metrics: ["profit_per_visitor_best", "profit_per_visitor_beat_control"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    hasMetricFilterIndex: true,
    showProfitPlaceholder: true,
  },
  {
    title: ["CVR Probability to be Best", "CVR Probability to beat Control"],
    metrics: ["conversion_best", "conversion_beat_control"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    hasMetricFilterIndex: true,
  },
  {
    title: ["AOV Probability to be Best", "AOV Probability to beat Control"],
    metrics: ["average_order_value_best", "average_order_value_beat_control"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "overview",
    graphType: GraphType.Bar,
    isGroup: false,
    hasMetricFilterIndex: true,
  },
  {
    groupTitle: "Opportunity Cost",
    isGroup: true,
    showGroupTitle: true,
    showFilter: true,
    groupFilterMetric: "VARIATION_ID",
    configs: [
      {
        title: ["Revenue Opportunity Cost", "Revenue Opportunity Cost"],
        metrics: ["value_per_visitor_best", "value_per_visitor_beat_control"],
        dataTypes: ["percent"],
        color: theme.palette.error.main,
        datasetId: "overview",
        graphType: GraphType.Line,
        filterMetric: "tolerance",
        isGroup: false,
        isFullWidth: true,
        isLossMetric: "revenue",
        hasMetricFilterIndex: true,
        yAxisLabel: ["Probability to be Best", "Probability to beat Control"],
        xAxisLabel: ["Tolerance %", "Tolerance %"],
        formatTooltipLabel: true,
      },
      {
        title: ["Profit Opportunity Cost", "Profit Opportunity Cost"],
        metrics: ["value_per_visitor_best", "value_per_visitor_beat_control"],
        dataTypes: ["percent"],
        color: theme.palette.error.main,
        datasetId: "overview",
        graphType: GraphType.Line,
        filterMetric: "tolerance",
        isGroup: false,
        isFullWidth: true,
        isLossMetric: "profit",
        hasMetricFilterIndex: true,
        yAxisLabel: ["Probability to be Best", "Probability to beat Control"],
        formatTooltipLabel: true,
        showProfitPlaceholder: true,
        xAxisLabel: ["Tolerance %", "Tolerance %"],
      },
    ],
  },
];

export const subscriptionsGraphConfig: DashboardGraphConfigs = [
  {
    title: ["Subscription Conversion Rate"],
    metrics: ["SUBSCRIPTION_ORDERS_PER_VISITOR"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.Bar,
    isGroup: false,
    allowThreeRow: true,
  },
  {
    title: ["Subscription Revenue per Visitor"],
    metrics: ["SUBSCRIPTION_REVENUE_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.Bar,
    isGroup: false,
    allowThreeRow: true,
  },
  {
    title: ["Subscription Profit per Visitor"],
    metrics: ["SUBSCRIPTION_PROFIT_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.Bar,
    isGroup: false,
    allowThreeRow: true,
    showProfitPlaceholder: true,
  },

  {
    title: ["Subscription % of Orders"],
    metrics: ["SUBSCRIPTION_PCT"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    title: ["Subscription AOV"],
    metrics: [
      "SUBSCRIPTION_AVG_PRODUCT_REVENUE_PER_ORDER",
      "SUBSCRIPTION_AVG_SHIPPING_REVENUE_PER_ORDER",
    ],
    stackedBarLegendTitles: [
      "Product Revenue Per Order",
      "Shipping Revenue Per Order",
    ],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.StackedBar,
    showAllStackedLabels: true,
    isGroup: false,
  },
  {
    title: ["Subscription Average Units per Order"],
    metrics: ["SUBSCRIPTION_AVG_UNITS_PER_ORDER"],
    dataTypes: ["none"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.Bar,
    isGroup: false,
  },
  {
    title: ["Subscription Product Revenue per Unit"],
    metrics: ["SUBSCRIPTION_AVG_PRODUCT_REV_PER_UNIT"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "subscriptions",
    graphType: GraphType.Bar,
    isGroup: false,
  },
];

export const alwaysOnIntervalGraphConfig: DashboardGraphConfigs = [
  {
    title: ["Visitors (web only)"], // DONE
    isFullWidth: true,
    metrics: ["VISITORS"],
    dataTypes: ["numberNoRounding"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.Line,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [{ key: "VISITORS", label: "unique" }],
    metricTableColumnKeys: ["VISITORS"],
    yAxisDataMin: 0,
  },
  {
    title: ["Percent Web Orders with IgId"],
    metrics: ["PCT_WEB_ORDERS_WITH_IGID"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.Line,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [{ key: "PCT_WEB_ORDERS_WITH_IGID", label: "aggregate" }],
    metricTableColumnKeys: ["PCT_WEB_ORDERS_WITH_IGID"],
    yAxisDataMin: 0,
  },
  {
    title: ["Orders"],
    metrics: ["ORDERS_WEB", "ORDERS_NON_WEB"],
    dataTypes: ["numberNoRounding"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.StackedArea,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [
      { key: "ORDERS", label: "total" },
      { key: "ORDERS_WEB", label: "web" },
    ],
    metricTableColumnKeys: ["ORDERS_WEB", "ORDERS_NON_WEB"],
    legendFormatFunction: uppercaseSnakeToNormal,
    yAxisDataMin: 0,
  },
  {
    title: ["Conversion (web only)"],
    isFullWidth: false,
    metrics: ["CONVERSION_RATE"],
    dataTypes: ["percent"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.Line,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [{ key: "TOTAL_CONVERSION_PCT", label: "aggregate" }],
    metricTableColumnKeys: ["TOTAL_CONVERSION_PCT"],
    legendFormatFunction: uppercaseSnakeToNormal,
    yAxisDataMin: 0,
  },
  {
    title: ["AOV"],
    isFullWidth: false,
    metrics: ["AOV_WEB", "AOV_NON_WEB"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.Line,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    showLegend: true,
    sumMetrics: [
      { key: "AOV_TOTAL", label: "total" },
      { key: "AOV_WEB", label: "web" },
    ],
    metricTableColumnKeys: ["AOV_WEB", "AOV_NON_WEB"],
    legendFormatFunction: (input: string) => {
      const words = input.split("_");

      // Capitalize each word except the first one
      const capitalizedWords = words.map((word, index) =>
        index === 0
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );

      // Join the capitalized words with spaces
      return capitalizedWords.join(" ");
    },
    yAxisDataMin: 0,
  },
  {
    title: ["Revenue"],
    metrics: ["NET_REVENUE_WEB", "NET_REVENUE_NON_WEB"],
    dataTypes: ["currencyNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.StackedArea,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [
      { key: "NET_REVENUE", label: "total" },
      { key: "NET_REVENUE_WEB", label: "web" },
    ],
    metricTableColumnKeys: ["NET_REVENUE_WEB", "NET_REVENUE_NON_WEB"],
    legendFormatFunction: uppercaseSnakeToNormal,
    yAxisDataMin: 0,
  },
  {
    title: ["Revenue per Visitor (web only)"],
    isFullWidth: false,
    metrics: ["REVENUE_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.Line,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [{ key: "REVENUE_PER_VISITOR_WEB", label: "aggregate" }],
    metricTableColumnKeys: ["REVENUE_PER_VISITOR_WEB"],
    yAxisDataMin: 0,
  },
  {
    title: ["Profit"],
    metrics: ["TOTAL_PROFIT_WEB", "TOTAL_PROFIT_NON_WEB"],
    dataTypes: ["currencyNoDecimal"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.StackedArea,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [
      { key: "TOTAL_PROFIT", label: "total" },
      { key: "TOTAL_PROFIT_WEB", label: "web" },
    ],
    metricTableColumnKeys: ["TOTAL_PROFIT_WEB", "TOTAL_PROFIT_NON_WEB"],
    legendFormatFunction: uppercaseSnakeToNormal,
    yAxisDataMin: 0,
  },
  {
    title: ["Profit per Visitor (web only)"],
    isFullWidth: false,
    metrics: ["PROFIT_PER_VISITOR"],
    dataTypes: ["currency"],
    color: theme.palette.error.main,
    datasetId: "alwaysOnInterval",
    graphType: GraphType.Line,
    filterMetric: "PERIOD_START_DT_LOCAL",
    isGroup: false,
    showSumMetric: true,
    showMoreDetails: true,
    sumMetrics: [{ key: "PROFIT_PER_VISITOR_WEB", label: "aggregate" }],
    metricTableColumnKeys: ["PROFIT_PER_VISITOR_WEB"],
    yAxisDataMin: 0,
  },
];
